import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Download, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportsPage() {
  const { apiCall } = useAuth();
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await apiCall('get', '/reports/export?format=csv', null, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'paf_compliance_report.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report exported');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="reports-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight mb-1">Reports</h1>
        <p className="text-[#64748B] mb-8">Export compliance data for audits and reporting</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
          className="bg-white rounded-2xl border border-indigo-100/50 p-6 md:p-8 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-base font-bold text-[#0F172A]">Compliance Report</h2>
              <p className="text-xs text-[#64748B]">CSV export of all employees and PAF status</p>
            </div>
          </div>
          <p className="text-sm text-[#64748B] mb-6">
            Export a comprehensive report including employee details, visa status, LCA expiration dates, and PAF compliance status.
          </p>
          <Button
            data-testid="export-csv-btn"
            onClick={handleExport}
            disabled={exporting}
            className="bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-full px-6 font-medium"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white rounded-2xl border border-indigo-100/50 p-6 md:p-8 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#FFFBEB] flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#D97706]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-base font-bold text-[#0F172A]">Audit Summary</h2>
              <p className="text-xs text-[#64748B]">Coming soon</p>
            </div>
          </div>
          <p className="text-sm text-[#64748B] mb-6">
            Generate detailed audit summaries with timestamped logs, document upload history, and compliance timeline.
          </p>
          <Button
            disabled
            variant="outline"
            className="rounded-full px-6 font-medium opacity-50 cursor-not-allowed"
          >
            Coming Soon
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
